import {
  Box,
  RadioGroupProps as ChRadioGroupProps,
  Flex,
  ResponsiveValue,
  SimpleGrid,
  useRadio,
  useRadioGroup,
} from '@chakra-ui/react'
import { FC, ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { blockSizes } from '../../theme/components'

type RadioVersion = 'v1' | 'v2'

export type Option = {
  name: string
  value: string
}

export type RadioProps = {
  isInvalid?: boolean
  children: ReactNode
  size?: keyof typeof blockSizes
  version?: RadioVersion
}

const v1Styling = {
  textStyle: 'cta',
  borderRadius: 'md',
  textAlign: 'center',
  justifyContent: 'center',
  _checked: {
    bg: 'softBlack',
    color: 'softWhite',
    cursor: 'default',
    _hover: { bg: 'softBlack', color: 'softWhite' },
  },
  _hover: { bg: 'white', borderColor: 'white', color: 'softBlack' },
}

const v2Styling = {
  borderColor: 'gray',
  color: 'softBlack',
  borderRadius: '4px',
  textAlign: 'left',
  justifyContent: 'start',
  _checked: {
    bg: 'faintOrange',
    borderColor: 'orange',
    cursor: 'default',
    _hover: { bg: 'faintOrange', borderColor: 'orange' },
  },
  _hover: { bg: 'faintOrange', borderColor: 'gray' },
}

export const Radio: FC<RadioProps> = ({
  children,
  isInvalid: _,
  size = 'md',
  version = 'v1',
  ...props
}) => {
  const { getInputProps, getCheckboxProps } = useRadio(props)
  const input = getInputProps()
  const checkbox = getCheckboxProps()
  const styling = version === 'v1' ? v1Styling : v2Styling

  return (
    <Box as="label" width="100%">
      <input {...input} />
      <Flex
        {...(checkbox as any)}
        {...blockSizes[size]}
        cursor="pointer"
        borderWidth={1}
        fontSize="sm"
        alignItems="center"
        _invalid={{ color: 'failRed', borderColor: 'failRed' }}
        {...styling}
      >
        <span>{children}</span>
      </Flex>
    </Box>
  )
}

export type BinaryInputProps = Omit<RadioGroupProps, 'options' | 'onChange'> & {
  onChange: (v: boolean) => void
  value: boolean
  options?: RadioGroupProps['options']
  size?: string
}

export const BinaryInput: FC<BinaryInputProps> = ({
  name,
  columns = 2,
  spacing = 'sm',
  size = 'md',
  onChange,
  value,
  isInvalid,
  options,
  dataCy,
}) => {
  const { formatMessage } = useIntl()
  const fallbackOptions = [
    { name: formatMessage({ defaultMessage: 'Yes' }), value: true },
    { name: formatMessage({ defaultMessage: 'No' }), value: false },
  ]

  const { getRootProps, getRadioProps } = useRadioGroup({
    name,
    defaultValue: `${value}`,
    onChange(v) {
      if (v === 'true') onChange(true)
      if (v === 'false') onChange(false)
    },
  })

  const group = getRootProps()

  return (
    <SimpleGrid
      columns={columns}
      spacing={spacing}
      {...(group as any)}
      data-cy={dataCy}
      width="100%"
    >
      {(options || fallbackOptions).map(({ name, value }) => {
        const radio = getRadioProps({ value: `${value}`, id: name })

        return (
          <Radio
            key={name}
            isInvalid={isInvalid}
            size={size}
            {...(radio as any)}
          >
            {name}
          </Radio>
        )
      })}
    </SimpleGrid>
  )
}

export type RadioGroupProps = {
  name: string
  options: Option[]
  onChange: ChRadioGroupProps['onChange']
  defaultValue?: string | string | null
  columns?: ResponsiveValue<number>
  spacing?: string
  size?: keyof typeof blockSizes
  isInvalid?: boolean
  value?: string | boolean | null
  dataCy?: string
  width?: string
  version?: RadioVersion
}

export const RadioGroup: FC<RadioGroupProps> = ({
  name,
  options,
  defaultValue,
  columns = 2,
  spacing = 8,
  size = 'md',
  onChange,
  isInvalid,
  dataCy,
  value,
  width = '100%',
  version,
}) => {
  const { getRootProps, getRadioProps } = useRadioGroup({
    name,
    defaultValue: defaultValue ?? undefined,
    onChange,
    value: typeof value !== 'undefined' ? `${value}` : value,
  })

  const group = getRootProps()

  return (
    <SimpleGrid
      data-cy={dataCy}
      columns={
        typeof columns === 'number' ? [Math.min(columns, 2), columns] : columns
      }
      spacing={spacing}
      width={width}
      {...(group as any)}
    >
      {options.map(({ name, value }: Option) => {
        const radio = getRadioProps({ value, id: name })

        return (
          <Radio
            isInvalid={isInvalid}
            key={value}
            {...(radio as any)}
            size={size}
            version={version}
          >
            {name}
          </Radio>
        )
      })}
    </SimpleGrid>
  )
}
